import { useMemo } from 'react';
import { SimpleGrid, HStack, Text, Icon, Center, Spinner, VStack, Image } from '@chakra-ui/react';

import SectionContainer from '~/containers/SectionContainer';
import EmptySection from '~/components/EmptySection';

import { PopularMealList, PopularRestaurantList } from './components';

import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import { useRecoilValue } from 'recoil';

import { IoRestaurantOutline, IoArrowForward } from 'react-icons/io5';

import { AiOutlineShop } from 'react-icons/ai';
import NoSearchResult from '~/assets/images/search.png';

import fp from 'lodash/fp';

import { FetchingDataStatus } from '~/types/utilities';

import { AlgoliaStore } from '~/types/store/store';
import { AlgoliaItem } from '~/types/store/item';

type SearchModeProps = {
  fetchingStatusOfSearchStore: FetchingDataStatus;
  fetchingStatusOfSearchItem: FetchingDataStatus;
  searchStores: Array<AlgoliaStore>;
  searchItemCount: number;
  searchItems: Array<AlgoliaItem>;
  onNavigateSearchItem: () => void;
  onNavigateSearchStore: () => void;
};

export default function SearchMode({
  fetchingStatusOfSearchStore,
  fetchingStatusOfSearchItem,
  searchStores,
  searchItemCount,
  searchItems,
  onNavigateSearchItem,
  onNavigateSearchStore,
}: SearchModeProps) {
  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);
  const pickMealAt = useRecoilValue(pickMealAtAtom);

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 12 }}>
      <SectionContainer
        title="餐廳"
        icon={<Icon as={AiOutlineShop} color="secondary" boxSize="1.25rem" />}
        rightHandComponent={
          isLargerThan768 ? (
            <></>
          ) : (
            <HStack align="center" spacing={1} cursor="pointer" onClick={onNavigateSearchStore}>
              <Text fontSize="xs" color="primary">
                查看更多
              </Text>
              <Icon color="primary" as={IoArrowForward} />
            </HStack>
          )
        }
      >
        <Text fontSize="sm" color="gray.500">
          符合搜尋結果: {searchStores.length} 個
        </Text>
        {fp.cond([
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.LOADING),
            }),
            () => (
              <Center>
                <Spinner size="lg" />
              </Center>
            ),
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.FAIL),
            }),
            () => <EmptySection content="獲取餐廳發生錯誤，請重試" />,
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.SUCCESS),
              storesLength: fp.eq(0),
            }),
            () => (
              <VStack spacing={6} py={5}>
                <Text color="fontColor" fontSize="sm">
                  查無結果
                </Text>
                <Image src={NoSearchResult} w={180} />
              </VStack>
            ),
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.SUCCESS),
              storesLength: fp.lt(0),
            }),
            () => (
              <PopularRestaurantList
                storeList={isLargerThan768 ? searchStores : searchStores.slice(0, 2)}
                pickMealTime={pickMealAt}
              />
            ),
          ],
          [fp.T, fp.always(null)],
        ])({ state: fetchingStatusOfSearchStore, storesLength: searchStores.length })}
      </SectionContainer>
      <SectionContainer
        title="餐點"
        icon={<Icon as={IoRestaurantOutline} color="secondary" boxSize="1.25rem" />}
        rightHandComponent={
          <HStack align="center" spacing={1} cursor="pointer" onClick={onNavigateSearchItem}>
            <Text fontSize="xs" color="primary">
              查看更多
            </Text>
            <Icon color="primary" as={IoArrowForward} />
          </HStack>
        }
      >
        <Text fontSize="sm" color="gray.500">
          符合搜尋結果: {searchItemCount || searchItems.length} 個
        </Text>
        {fp.cond([
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.LOADING),
            }),
            () => (
              <Center>
                <Spinner size="lg" />
              </Center>
            ),
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.FAIL),
            }),
            () => <EmptySection content="獲取資料發生錯誤請重試" />,
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.SUCCESS),
              itemsLength: fp.eq(0),
            }),
            () => (
              <VStack spacing={6} py={5}>
                <Text color="fontColor" fontSize="sm">
                  查無結果
                </Text>
                <Image src={NoSearchResult} w={180} />
              </VStack>
            ),
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.SUCCESS),
              itemsLength: fp.lt(0),
            }),
            () => <PopularMealList items={searchItems} />,
          ],
          [fp.T, fp.always(null)],
        ])({
          state: fetchingStatusOfSearchItem,
          itemsLength: searchItemCount || searchItems.length,
        })}
      </SectionContainer>
    </SimpleGrid>
  );
}
